import { Typography } from '@mui/material';
import { MUIDataTableOptions } from 'mui-datatables';
import React from 'react';
import { ClientSideDataTable } from 'src/common/components/dataTable/clientSide';
import { formatDollars } from '../../../../common';
import { DatatableColumn, FilterDataType } from '../../../../common/components/dataTable/types';
import { LoadingProgress } from '../../../../common/store/types';
import { theme } from '../../../../themes';
import { ModelAssociationItem } from '../store/types';

export interface Props {
  progress: LoadingProgress;
  data: ModelAssociationItem[];
}
export function AssociationsTable({ progress, data }: Props): JSX.Element {
  const defaultItems: ModelAssociationItem[] = [];

  const columns: DatatableColumn[] = [
    {
      label: 'AFSL NAME',
      name: 'afslName',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].afslName : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'PRACTICE NAME',  
      name: 'practiceName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'ADVISER NAME',  
      name: 'adviserName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'CLIENT ID',
      name: 'clientId',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].clientId : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'CLIENT NAME',  
      name: 'clientName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'PORTFOLIO ID',
      name: 'investmentServiceCode',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].investmentServiceCode : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'PORTFOLIO NAME',  
      name: 'portfolioName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'MODEL CODE',  
      name: 'modelCode',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      name: 'clientName',
      label: 'CLIENT AND PORTFOLIO NAME',
      textAlign: 'left',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <>
              <Typography variant={'h5'} align="left">
                {data[dataIndex] !== undefined ? data[dataIndex].clientName : ''}
              </Typography>
              <Typography color={'textSecondary'} variant={'h6'} align="left">
                {data[dataIndex] !== undefined ? data[dataIndex].investmentServiceName : ''}
              </Typography>
            </>
          );
        },
        download: false,     
        sort: true,
      },
      filterDataType: FilterDataType.string,
    },
    {
      filterDataType: FilterDataType.string,
      textAlign: 'right',
      label: 'VALUE',
      name: 'value',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
           return <Typography variant="h5">{formatDollars(data[dataIndex]?.value)}</Typography>;
        },
        sort: false,
        viewColumns: false,
      },
    },
  ];

  const options: MUIDataTableOptions = {
    search: true,
    download: true,
    filter: false,
  };

  const tableData = (data && data.length > 0 && !progress.isLoading ? data : defaultItems).map(item => ({
    ...item,
    priceDate: item.priceDate,
    practiceName: item.practiceName,
    adviserName: item.adviserName,
    clientName: item.clientName,
    portfolioName: item.investmentServiceName,
    accountName: item.accountName,
    unitPrice: item.unitPrice,
    units: item.units,
    afslName: item.afslName,
    modelCode: item.modelCode,
    value: item.value
  }));

  return (
    <ClientSideDataTable
      data={tableData}
      columns={columns}
      options={options}
      loadingProgress={progress}
      id="model-associations-datatable"
    />
  );
}
